import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { createWedgeDetector, resolveScanResult, scanInputOwned } from './scan';

/**
 * Mounted once in AppShell: keyboard-wedge burst detection (≥ 6 characters within 100 ms + Enter) anywhere in the app.
 * Every burst goes through `GET /barcode/resolve` and lands on the route the API returns — the item card for an `ASS-` label
 * or a bare serial, the part for `ASP-` or a raw P/N, the bin for `ASL-`, the tag for `AST-`, the document for `ASD-`;
 * an `ASU-` badge and anything the system does not know open the lookup screen with the code in the box.
 * Inputs marked `data-scan-input` (the top-bar scan box, the pickers) handle their own Enter and are skipped here, and
 * a `/scan` flow that has claimed the wedge (scanInputOwned) takes every burst itself — the scan must not both fill its
 * cart and navigate away.
 */
export function useGlobalScan(): void {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { message } = App.useApp();
  useEffect(() => {
    const detect = createWedgeDetector((code) => {
      void resolveScanResult(code).then((hit) => {
        void nav(hit.route);
        if (hit.found) void message.info(t('scan.scanned', { code }));
        else void message.warning(t('scan.notFound', { code }));
      });
    });
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('[data-scan-input]')) return;
      if (scanInputOwned()) return;
      detect(e);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [nav, t, message]);
}
